/* crm-phase7-views.jsx — Phase 7 Integration: Jobs, Prompt Templates, AI Sessions
   Provides: JobsView, PromptTemplatesView, AiSessionsView
   All self-register on window. */

const { useState, useEffect, useCallback } = React;

import * as aiGatewayApi from '../../api/ai-gateway.api.js';

const _statusColor = (s) => ({ PENDING: '#F59E0B', RUNNING: '#3B82F6', COMPLETED: '#16A34A', FAILED: '#EF4444', CANCELLED: '#6B7280' }[s] || '#6B7280');
const StatusPill = ({ status }) => {
  const c = _statusColor(status);
  return <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: c + '18', color: c }}>{status}</span>;
};

/* ── Jobs View ── */
const JobsView = () => {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState('');
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;

  const load = useCallback(async () => {
    if (!wsId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await window.__nx_api.jobs.list(wsId, filter || undefined);
      setJobs(Array.isArray(data) ? data : (data?.jobs ?? []));
    } catch (err) {
      setError(err.message || 'Failed to load jobs');
    }
    setLoading(false);
  }, [wsId, filter]);

  useEffect(() => { load(); }, [load]);

  const FILTERS = ['', 'PENDING', 'RUNNING', 'COMPLETED', 'FAILED'];

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <span style={{ fontWeight: 700, fontSize: 20, color: 'var(--nx-text)' }}>Background Jobs</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          {FILTERS.map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{ padding: '4px 12px', borderRadius: 20, border: '1px solid var(--nx-border)', background: filter === s ? 'var(--nx-accent)' : 'transparent', color: filter === s ? '#fff' : 'var(--nx-text-muted)', fontSize: 11, cursor: 'pointer', fontWeight: filter === s ? 700 : 400 }}>{s || 'All'}</button>
          ))}
          <button onClick={load} title="Refresh" style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'transparent', fontSize: 11, cursor: 'pointer', color: 'var(--nx-text-muted)' }}>↻</button>
        </div>
      </div>

      {loading && <div style={{ padding: 24, color: 'var(--nx-text-muted)' }}>Loading jobs…</div>}
      {error   && <div style={{ padding: 24, color: 'var(--nx-error, #ef4444)', fontSize: 13 }}>{error}</div>}
      {!loading && !error && jobs.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>✓</div>
          No {filter || ''} jobs found.
        </div>
      )}

      {!loading && jobs.map(job => (
        <div key={job.id} style={{ background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: '12px 16px', marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{job.type || job.name || job.id}</div>
            {job.entityType && (
              <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{job.entityType}{job.entityId ? ` · ${String(job.entityId).slice(0, 8)}` : ''}</div>
            )}
            {job.error && <div style={{ fontSize: 11, color: 'var(--nx-error, #ef4444)', marginTop: 4 }}>{job.error}</div>}
          </div>
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <StatusPill status={job.status} />
            <span style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>{job.createdAt ? new Date(job.createdAt).toLocaleString() : ''}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ── Prompt Templates View ── */
const PromptTemplatesView = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState({ name: '', slug: '', content: '', description: '' });
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast?.();

  const load = useCallback(async () => {
    if (!wsId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await aiGatewayApi.listTemplates(wsId);
      setTemplates(Array.isArray(data) ? data : (data?.templates ?? []));
    } catch (err) {
      setError(err.message || 'Failed to load templates');
    }
    setLoading(false);
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.name || !form.slug || !form.content || !wsId) return;
    try {
      if (editing) {
        const updated = await aiGatewayApi.updateTemplate(wsId, editing.id, form);
        setTemplates(prev => prev.map(t => t.id === editing.id ? (updated || { ...t, ...form }) : t));
      } else {
        const created = await aiGatewayApi.createTemplate(wsId, form);
        if (created) setTemplates(prev => [created, ...prev]);
      }
      setEditing(null);
      setForm({ name: '', slug: '', content: '', description: '' });
      toast?.({ tone: 'success', icon: 'Check', title: editing ? 'Template updated' : 'Template created' });
    } catch (err) {
      toast?.({ tone: 'error', icon: 'AlertCircle', title: 'Failed to save', desc: err.message });
    }
  };

  const handleDelete = async (id) => {
    if (!wsId) return;
    try {
      await aiGatewayApi.deleteTemplate(wsId, id);
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast?.({ tone: 'success', icon: 'Trash', title: 'Template deleted' });
    } catch (err) {
      toast?.({ tone: 'error', icon: 'AlertCircle', title: 'Delete failed', desc: err.message });
    }
  };

  const startEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, slug: t.slug, content: t.content, description: t.description || '' });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm({ name: '', slug: '', content: '', description: '' });
  };

  const INPUT = { padding: '6px 10px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 12, width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <span style={{ fontWeight: 700, fontSize: 20, color: 'var(--nx-text)' }}>Prompt Templates</span>
        {!editing && (
          <button onClick={() => setEditing({})} style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 7, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ New Template</button>
        )}
      </div>

      {(editing !== null) && (
        <div style={{ background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-accent)', borderRadius: 10, padding: 20, marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{editing?.id ? 'Edit Template' : 'New Template'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 4 }}>NAME</div>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Template name" style={INPUT} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 4 }}>SLUG</div>
              <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} placeholder="unique-slug" style={INPUT} />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 4 }}>DESCRIPTION</div>
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional description" style={INPUT} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 4 }}>CONTENT</div>
            <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Prompt content. Use {{variable}} for dynamic values." rows={5} style={{ ...INPUT, resize: 'vertical', fontFamily: 'monospace' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={cancelEdit} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'transparent', color: 'var(--nx-text-muted)', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSave} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Save</button>
          </div>
        </div>
      )}

      {loading && <div style={{ padding: 24, color: 'var(--nx-text-muted)' }}>Loading templates…</div>}
      {error   && <div style={{ padding: 24, color: 'var(--nx-error, #ef4444)', fontSize: 13 }}>{error}</div>}
      {!loading && !error && templates.length === 0 && !editing && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13 }}>No prompt templates yet. Create one to get started.</div>
      )}

      {templates.map(t => (
        <div key={t.id} style={{ background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: '14px 16px', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{t.name}</div>
              <div style={{ fontSize: 11, color: 'var(--nx-accent)', fontFamily: 'monospace', marginTop: 2 }}>{t.slug}</div>
              {t.description && <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 4 }}>{t.description}</div>}
              {t.content && <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--nx-text-muted)', marginTop: 6, background: 'var(--nx-bg)', borderRadius: 4, padding: '6px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 600 }}>{t.content}</div>}
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button onClick={() => startEdit(t)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'transparent', color: 'var(--nx-text-muted)', fontSize: 11, cursor: 'pointer' }}>Edit</button>
              <button onClick={() => handleDelete(t.id)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #EF444433', background: 'transparent', color: '#EF4444', fontSize: 11, cursor: 'pointer' }}>Delete</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ── AI Sessions View ── */
const AiSessionsView = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;

  const load = useCallback(async () => {
    if (!wsId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await aiGatewayApi.listSessions(wsId);
      setSessions(Array.isArray(data) ? data : (data?.sessions ?? []));
    } catch (err) {
      setError(err.message || 'Failed to load sessions');
    }
    setLoading(false);
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  const fmtTime = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <span style={{ fontWeight: 700, fontSize: 20, color: 'var(--nx-text)' }}>AI Sessions</span>
        <div style={{ marginLeft: 'auto' }}>
          <button onClick={load} style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'transparent', fontSize: 11, cursor: 'pointer', color: 'var(--nx-text-muted)' }}>Refresh</button>
        </div>
      </div>

      {loading && <div style={{ padding: 24, color: 'var(--nx-text-muted)' }}>Loading sessions…</div>}
      {error   && <div style={{ padding: 24, color: 'var(--nx-error, #ef4444)', fontSize: 13 }}>{error}</div>}
      {!loading && !error && sessions.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13 }}>No AI sessions yet.</div>
      )}

      {!loading && sessions.map(s => (
        <div key={s.id} style={{ background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: '12px 16px', marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{s.title || s.id}</div>
            {s.provider && <div style={{ fontSize: 11, color: 'var(--nx-accent)', marginBottom: 2 }}>{s.provider}</div>}
            {s.lastMessage && (
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 600 }}>{s.lastMessage}</div>
            )}
          </div>
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            {s.messageCount != null && (
              <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', background: 'var(--nx-border)', borderRadius: 10, padding: '2px 8px' }}>{s.messageCount} msgs</span>
            )}
            <span style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>{fmtTime(s.updatedAt || s.createdAt)}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

window.JobsView          = JobsView;
window.PromptTemplatesView = PromptTemplatesView;
window.AiSessionsView    = AiSessionsView;
